import { useCallback, useState } from 'react'
import Cropper, { type Area } from 'react-easy-crop'
import { Button } from '@cocoonspace/ui/components/button'
import {
	Dialog,
	DialogContent,
	DialogTitle,
	DialogTrigger,
} from '@cocoonspace/ui/components/dialog'
import { Slider } from '@cocoonspace/ui/components/slider'
import { useLingui } from '@lingui/react/macro'
import { Trash2Icon, UploadIcon } from 'lucide-react'
import Image from 'next/image'

import { AvatarPreview } from './avatar-preview'
import { ImageDropzone } from './image-dropzone'
import { base64ToBlob } from './utils/base64-to-blob'
import { getCroppedImageBase64, type OutputSize } from './utils/canvas-utils'
import {
	type ProfileImageKind,
	useUploadProfileImage,
} from './utils/use-upload-profile-image'

const DialogCropper = ({
	imageSrc,
	kind,
	outputSize,
	onChange,
	onPreviewChange,
}: {
	imageSrc: string
	kind: ProfileImageKind
	outputSize?: OutputSize
	onChange: (value: string) => void
	onPreviewChange: (value: string) => void
}) => {
	const { t } = useLingui()
	const [crop, setCrop] = useState({ x: 0, y: 0 })
	const [zoom, setZoom] = useState(1.5)
	const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
	const [isUploading, setIsUploading] = useState(false)

	const { uploadImage } = useUploadProfileImage()
	const aspect = kind === 'banner' ? 70 / 11 : kind === 'background' ? 16 / 9 : 1

	const onCropComplete = (_croppedArea: Area, croppedAreaPixels: Area) => {
		setCroppedAreaPixels(croppedAreaPixels)
	}

	const handleValidate = useCallback(async () => {
		if (!croppedAreaPixels) return

		try {
			const base64 = await getCroppedImageBase64(
				imageSrc,
				croppedAreaPixels,
				outputSize,
			)

			const blob = base64ToBlob(base64)
			setIsUploading(true)
			const path = await uploadImage(blob, kind)

			if (!path) {
				throw new Error('Failed to upload profile image')
			}

			onChange(path)
			onPreviewChange(base64)
		} catch (error) {
			console.error(error)
		} finally {
			setIsUploading(false)
		}
	}, [
		imageSrc,
		kind,
		outputSize,
		croppedAreaPixels,
		onChange,
		onPreviewChange,
		uploadImage,
	])

	return (
		<div className='flex flex-col gap-6'>
			<div className='relative h-[300px] w-full overflow-hidden rounded-md'>
				<Cropper
					image={imageSrc}
					cropShape={kind === 'avatar' ? 'round' : 'rect'}
					objectFit='contain'
					aspect={aspect}
					crop={crop}
					zoom={zoom}
					onCropChange={setCrop}
					onZoomChange={setZoom}
					onCropComplete={onCropComplete}
				/>
			</div>

			<div className='relative flex items-center gap-4'>
				<span className='text-muted-foreground mr-auto text-sm'>ZOOM</span>

				<Slider
					value={[zoom]}
					onValueChange={(value) => setZoom(value[0])}
					min={1}
					max={3}
					step={0.1}
					aria-labelledby='Zoom'
					className='flex-1'
				/>
			</div>

			<Button
				size='sm'
				className='w-full'
				disabled={isUploading}
				onClick={handleValidate}
			>
				{isUploading ? t`Traitement en cours...` : t`Valider`}
			</Button>
		</div>
	)
}

const CropDialogContent = ({
	kind,
	outputSize,
	onChange,
	onPreviewChange,
}: {
	kind: ProfileImageKind
	outputSize?: OutputSize
	onChange: (value: string) => void
	onPreviewChange: (value: string) => void
}) => {
	const [imageSrc, setImageSrc] = useState<string | null>()

	if (!imageSrc) return <ImageDropzone onChange={setImageSrc} />

	return (
		<DialogCropper
			imageSrc={imageSrc}
			kind={kind}
			outputSize={outputSize}
			onChange={onChange}
			onPreviewChange={onPreviewChange}
		/>
	)
}

export const ImagePicker = ({
	kind = 'avatar',
	value,
	preview,
	outputSize,
	onChange,
	onPreviewChange,
}: {
	kind?: ProfileImageKind
	value: string
	preview?: string
	outputSize?: OutputSize
	onChange: (value: string) => void
	onPreviewChange: (value: string) => void
}) => {
	const { t } = useLingui()
	const [isOpen, setIsOpen] = useState(false)

	return (
		<div className='flex w-full flex-wrap items-center gap-3'>
			{kind === 'avatar' ? (
				<AvatarPreview avatarUrl={preview} />
			) : preview ? (
				<Image
					src={preview}
					alt=''
					width={kind === 'banner' ? 350 : kind === 'background' ? 128 : 72}
					height={kind === 'banner' ? 55 : kind === 'background' ? 72 : 72}
					unoptimized
					className='max-w-full shrink-0 rounded border object-cover'
				/>
			) : null}

			<Dialog
				open={isOpen}
				onOpenChange={setIsOpen}
			>
				<DialogTrigger asChild>
					<Button
						size='sm'
						variant='outline'
					>
						<UploadIcon className='mr-1 size-4' />
						{value ? t`Modifier` : t`Charger une photo`}
					</Button>
				</DialogTrigger>

				<DialogContent
					className='max-w-lg gap-0'
					hideCloseBtn
				>
					<DialogTitle>{t`Modifier l’image`}</DialogTitle>

					<CropDialogContent
						kind={kind}
						outputSize={outputSize}
						onPreviewChange={onPreviewChange}
						onChange={(value) => {
							onChange(value)
							setIsOpen(false)
						}}
					/>
				</DialogContent>
			</Dialog>

			{!!value && (
				<Button
					size='sm'
					variant='outline'
					onClick={() => onChange('')}
				>
					<Trash2Icon className='mr-1 size-4' />
					{t`Supprimer`}
				</Button>
			)}
		</div>
	)
}
